import { Component, Prop, h, State, Host, Element, Watch } from '@stencil/core';
import { awaitTopbar } from '@/globals/helpers';
import { PnLanguages } from '@/globals/types';

/**
 * There are no events or features other than the `icon`, `small` and `language` prop.
 *
 * Our rule is to not have disabled buttons.
 * However, this component is an exception for a very specific use case regarding portal users.
 */
@Component({
  tag: 'pn-read-only-button',
  styleUrl: 'pn-read-only-button.scss',
})
export class PnReadOnlyButton {
  @Element() hostElement: HTMLElement;

  @State() readOnlyModeText = '';
  @State() tooltipOpen: boolean = false;
  @State() tooltipEl: HTMLDivElement;
  @State() tooltipDx: string;

  /** The SVG content of the icon you want. */
  @Prop() icon?: string;
  /** Icon on the left side instead of on the right. */
  @Prop() leftIcon: boolean = false;
  /** If you're using a button with only an icon, button text is still REQUIRED for the tooltip. */
  @Prop() iconOnly: boolean = false;
  /** Smaller button. */
  @Prop() small: boolean = false;
  /** Set the language manually. */
  @Prop() language?: PnLanguages = null;

  @Watch('language')
  setReadOnlyModeText() {
    const translations = {
      en: 'You cannot complete this action while in read only mode',
      sv: 'Du kan inte utföra denna handling i skrivskyddat läge',
      da: 'Du kan ikke gennemføre denne handling i skrivebeskyttet tilstand',
      fi: 'Et voi suorittaa tätä toimintoa vain luku -tilassa',
      no: 'Du kan ikke utføre denne handlingen i skrivebeskyttet modus',
    };

    this.readOnlyModeText = translations[this.language];
  }

  async componentWillLoad() {
    this.setReadOnlyModeText();

    if (this.language) return;
    await awaitTopbar(this.hostElement);
  }

  componentDidLoad() {
    this.initTooltip();
  }

  /*---------------------------------------TOOLTIP LOGIC-------------------------------------------*/

  @Watch('tooltipOpen')
  openHandler(): void {
    if (this.tooltipOpen) {
      this.checkTooltipPosition();
      return;
    }
    this.setTooltipOffset();
  }

  initTooltip(): void {
    this.tooltipEl = this.hostElement.querySelector('.button-tooltip');
    const tooltipAnchor = this.hostElement;

    const openEvents = ['mouseenter', 'focus'];
    const closeEvents = ['mouseleave', 'blur'];

    const eventListeners = [...openEvents, ...closeEvents];

    eventListeners.forEach(e => {
      tooltipAnchor.addEventListener(e, event => {
        // Should open?
        if (openEvents.includes(event.type)) {
          this.tooltipOpen = true;
          return;
        }
        // Should close?
        if (closeEvents.includes(event.type)) {
          this.tooltipOpen = false;
        }
      });
    });
  }

  setTooltipOffset(): void {
    this.tooltipEl.style.transform = `translateX(-50%) translateX(${this.tooltipDx}px)`;
  }

  checkTooltipPosition(): void {
    this.tooltipEl.style.transform = 'translateX(-50%)';

    requestAnimationFrame(() => {
      const { x, right } = this.tooltipEl.getBoundingClientRect();
      const extraMargin = 8;
      this.tooltipDx = '0';
      this.tooltipEl.style.transform = '';

      if (x < 0) this.tooltipDx = `${x * -1 + extraMargin}`;
      if (right > window.innerWidth) this.tooltipDx = `${window.innerWidth - right - extraMargin}`;

      requestAnimationFrame(() => {
        this.setTooltipOffset();
      });
    });
  }
  /* ---------------------------------------/TOOLTIP--------------------------------------- */

  getClassNames(): string {
    let classNames = 'pn-button ';

    if (this.small) classNames += 'pn-button-small ';
    if (this.leftIcon) classNames += 'pn-button-left-icon ';
    if (this.iconOnly) classNames += 'icon-only ';

    return classNames;
  }

  render() {
    return (
      <Host class="pn-read-only-button">
        <button class={this.getClassNames()} data-tooltip-open={this.tooltipOpen} disabled={true}>
          {!this.iconOnly && <slot />}
          {this.icon && <pn-icon icon={this.icon} color="gray700" />}

          {this.iconOnly && (
            <span class="read-only-icon-only">
              <slot />
            </span>
          )}

          <div class="button-tooltip">{this.readOnlyModeText}</div>
        </button>
      </Host>
    );
  }
}
